// Kostaja services data. Editable. Source of truth for marketplace + detail pages.
// Each service follows the same shape so detail pages render consistently.
//
// cta.kind values:
//   "book"    -> Calendly
//   "contact" -> /contact?service=<slug>
//   "stripe"  -> stripePaymentLink (falls back to contact if blank)
//   "shop"    -> Shopify SHOP_URL
//   "quote"   -> /contact?service=<slug>&intent=quote
//
// Prices are in CAD. Some are fixed, some are "Starting at" with billing="project".

export const SERVICES = [
  // ----------------------- STRATEGY AND AUDITS -----------------------
  {
    slug: "strategy-call",
    name: "Strategy Call",
    category: "strategy-and-audits",
    price: 250,
    priceLabel: "CA$250",
    billing: "one-time",
    outcome:
      "Leave the call knowing the single best next step for your business, so you stop guessing and stop paying for the wrong project.",
    bestFor:
      "Business owners who feel stuck, unsure, scattered, or uncertain which service to choose.",
    whatYouReceive: [
      "30-minute strategy session",
      "Phone or video format",
      "Review of current situation",
      "Discussion of brand, website, marketing, offer, or launch needs",
      "Practical next-step recommendations",
      "Clear follow-up direction",
    ],
    whatThisHelpsFix: [
      "Uncertainty about the right next step",
      "Spending money on services that do not fit the actual problem",
      "Scattered brand, website, and marketing decisions",
    ],
    notIncluded: [
      "Full brand strategy",
      "Full website audit",
      "Written report unless added separately",
      "Design work",
      "Implementation",
      "Ongoing support",
    ],
    whatWeNeed: [
      "Business name",
      "Website or social links",
      "Brief context",
      "Main problem or decision",
      "Timeline or urgency",
    ],
    timeline:
      "Call happens at booked Calendly time. Follow-up direction may be provided after the call.",
    process: [
      "Book your time on Calendly",
      "Send a short context note before the call",
      "Meet for a 30 minute working session",
      "Receive a clear next-step direction",
    ],
    cta: { label: "Start a Project", kind: "book" },
    stripePaymentLink: "",
    related: {
      services: ["brand-audit", "website-audit", "brand-and-website-review"],
      needs: ["not-sure-where-to-start", "outgrew-our-brand"],
      industries: [],
    },
    tags: ["strategy", "consult", "call"],
  },
  {
    slug: "brand-audit",
    name: "Brand Audit",
    category: "strategy-and-audits",
    price: 350,
    priceLabel: "CA$350",
    billing: "one-time",
    outcome:
      "See exactly why your brand is losing trust, and get a short list of fixes that will make it feel sharper, more credible, and worth more.",
    bestFor:
      "Businesses that look inconsistent, outdated, unclear, generic, or smaller than they really are.",
    whatYouReceive: [
      "Review of logo, colours, typography, and visual consistency",
      "Review of messaging and public-facing positioning",
      "Website and social presence scan where applicable",
      "Notes on trust signals, professionalism, and recognition",
      "Priority recommendations",
      "Written summary",
    ],
    whatThisHelpsFix: [
      "Inconsistent or outdated brand appearance",
      "Generic positioning",
      "Looking smaller than the business actually is",
    ],
    notIncluded: [
      "Logo design",
      "Full rebrand",
      "Website redesign",
      "Copywriting",
      "Social media management",
      "Implementation",
    ],
    whatWeNeed: [
      "Website link",
      "Social links",
      "Logo or brand files if available",
      "Any existing brand materials",
      "Notes about what feels wrong or outdated",
    ],
    timeline: "Typically 5 to 7 business days after materials are received.",
    process: [
      "Purchase or request the audit",
      "Send brand and link materials",
      "Kostaja reviews and prepares the summary",
      "Receive written summary and recommendations",
    ],
    cta: { label: "Request Brand Audit", kind: "stripe" },
    stripePaymentLink: "",
    related: {
      services: ["brand-foundation-starter", "brand-and-website-review", "strategy-call"],
      needs: ["outgrew-our-brand", "look-more-credible"],
      industries: [],
    },
    tags: ["audit", "brand"],
  },
  {
    slug: "website-audit",
    name: "Website Audit",
    category: "strategy-and-audits",
    price: 450,
    priceLabel: "CA$450",
    billing: "one-time",
    outcome:
      "Find out why your website is not turning visitors into customers, and get a clear list of the fixes that will actually move the needle.",
    bestFor:
      "Businesses with a website that exists but does not sell, explain, guide, or convert well enough.",
    whatYouReceive: [
      "Homepage review",
      "Navigation and page flow scan",
      "Messaging and offer review",
      "Mobile experience notes",
      "Trust signal review",
      "CTA and conversion path review",
      "Written summary",
      "Priority recommendations",
    ],
    whatThisHelpsFix: [
      "Website that does not sell or convert",
      "Confusing navigation",
      "Weak trust signals",
      "Unclear offer or next step",
    ],
    notIncluded: [
      "Website redesign",
      "Development",
      "SEO campaign",
      "Copywriting implementation",
      "Technical rebuild",
      "Analytics setup unless quoted separately",
    ],
    whatWeNeed: [
      "Website URL",
      "Important service or product links",
      "Main conversion goal",
      "Known issues or concerns",
      "Access to analytics only if specifically requested",
    ],
    timeline: "Typically 5 to 7 business days after materials are received.",
    process: [
      "Purchase or request the audit",
      "Send website link and goal",
      "Kostaja reviews and prepares the summary",
      "Receive written summary and recommendations",
    ],
    cta: { label: "Request Website Audit", kind: "stripe" },
    stripePaymentLink: "",
    related: {
      services: ["website-foundation-starter", "website-growth-build", "brand-and-website-review"],
      needs: ["website-doesnt-convert", "look-more-credible"],
      industries: [],
    },
    tags: ["audit", "website"],
  },
  {
    slug: "brand-and-website-review",
    name: "Brand and Website Review",
    category: "strategy-and-audits",
    price: 750,
    priceLabel: "CA$750",
    billing: "one-time",
    outcome:
      "See how your brand and website are working together, where they are quietly costing you leads, and what to fix first.",
    bestFor:
      "Businesses that suspect their brand, website, offer, and message are not working together.",
    whatYouReceive: [
      "Brand identity review",
      "Website structure review",
      "Messaging and offer scan",
      "Trust and consistency notes",
      "CTA and conversion review",
      "Written summary",
      "Priority recommendations",
      "Recommended next project path",
    ],
    whatThisHelpsFix: [
      "Brand and website that feel disconnected",
      "Messaging that does not match the visuals",
      "Unclear path from interest to action",
    ],
    notIncluded: [
      "Full rebrand",
      "Website redesign",
      "New copywriting",
      "Implementation",
      "Ongoing marketing",
    ],
    whatWeNeed: [
      "Website URL",
      "Social links",
      "Brand files if available",
      "Main services or offers",
      "Main customer type",
      "Known challenges",
    ],
    timeline: "Typically 7 to 10 business days after materials are received.",
    process: [
      "Purchase or request the review",
      "Send brand and website materials",
      "Kostaja reviews and prepares the combined summary",
      "Receive written summary and recommended next project path",
    ],
    cta: { label: "Request Review", kind: "stripe" },
    stripePaymentLink: "",
    related: {
      services: [
        "brand-and-website-launch-foundation",
        "brand-and-website-growth-system",
        "brand-foundation-core",
      ],
      needs: ["look-more-credible", "rebrand-and-relaunch"],
      industries: [],
    },
    tags: ["audit", "brand", "website"],
  },
  {
    slug: "local-seo-snapshot",
    name: "Local SEO Snapshot",
    category: "strategy-and-audits",
    price: 350,
    priceLabel: "CA$350",
    billing: "one-time",
    outcome:
      "See why local customers are not finding you, and get a short list of moves that will make you easier to discover in your area.",
    bestFor:
      "Local businesses that want more visibility and better local discovery signals.",
    whatYouReceive: [
      "Local presence review",
      "Website and Google Business Profile scan",
      "Service keyword notes",
      "Location signal review",
      "Opportunity summary",
      "Written recommendations",
    ],
    whatThisHelpsFix: [
      "Weak local discoverability",
      "Inconsistent location signals",
      "Missing service or category coverage",
    ],
    notIncluded: [
      "Ongoing SEO campaign",
      "Guaranteed rankings",
      "Google Business Profile implementation",
      "Local citation cleanup",
      "Review generation",
      "Website rebuild",
    ],
    whatWeNeed: [
      "Website URL",
      "Google Business Profile link if available",
      "Service area",
      "Main services",
      "Top competitors if known",
    ],
    timeline: "Typically 5 to 7 business days.",
    process: [
      "Request the snapshot",
      "Send website and local profile links",
      "Kostaja reviews and prepares the snapshot",
      "Receive written summary and recommendations",
    ],
    cta: { label: "Request Local SEO Snapshot", kind: "contact" },
    stripePaymentLink: "",
    related: {
      services: [
        "google-business-profile-audit",
        "google-business-profile-optimization",
        "local-visibility-starter-kit",
      ],
      needs: ["more-local-customers"],
      industries: ["local-services", "hospitality"],
    },
    tags: ["audit", "local", "seo"],
  },

  // ----------------------- BRAND SYSTEMS -----------------------
  {
    slug: "brand-foundation-starter",
    name: "Brand Foundation Starter",
    category: "brand-systems",
    price: 1500,
    priceLabel: "Starting at CA$1,500",
    billing: "project",
    outcome:
      "Look credible from day one, without waiting months or spending five figures to get there.",
    bestFor:
      "Early-stage businesses, small businesses, solo operators, and local brands that need a professional starting point.",
    whatYouReceive: [
      "Brand direction call",
      "Logo refinement or simple identity direction",
      "Core colour direction",
      "Typography direction",
      "Basic visual style direction",
      "Simple brand snapshot",
      "Essential file exports where applicable",
      "Usage notes",
    ],
    whatThisHelpsFix: [
      "Looking unprofessional or generic",
      "No clear visual direction",
      "Inconsistent brand assets",
    ],
    notIncluded: [
      "Full brand strategy",
      "Naming",
      "Trademark research",
      "Large logo suite",
      "Extensive guidelines",
      "Custom illustration",
      "Packaging",
      "Website design",
    ],
    whatWeNeed: [
      "Business name",
      "Existing logo if any",
      "Business description",
      "Target customer",
      "Style preferences",
      "Examples you like or dislike",
    ],
    timeline: "Typically 1 to 2 weeks after kickoff and materials.",
    process: [
      "Request the project",
      "Kickoff direction call",
      "Direction review",
      "Refinement and final exports",
    ],
    cta: { label: "Request Brand Foundation Starter", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["brand-foundation-core", "brand-foundation-complete", "brand-audit"],
      needs: ["look-more-credible", "starting-a-new-business"],
      industries: ["local-services", "retail", "professional-services"],
    },
    tags: ["brand", "identity"],
  },
  {
    slug: "brand-foundation-core",
    name: "Brand Foundation Core",
    category: "brand-systems",
    price: 2500,
    priceLabel: "Starting at CA$2,500",
    billing: "project",
    outcome:
      "Show up consistently across every touchpoint, so customers stop questioning whether you are the real deal.",
    bestFor:
      "Businesses that need a stronger identity, clearer first impression, and practical brand assets.",
    whatYouReceive: [
      "Discovery and direction call",
      "Positioning notes",
      "Logo suite or logo system",
      "Colour palette",
      "Typography system",
      "Visual identity direction",
      "Core messaging notes",
      "Social profile direction",
      "Essential brand guide",
      "Exported logo and brand files",
    ],
    whatThisHelpsFix: [
      "Weak first impression",
      "Inconsistent brand presence across channels",
      "No working brand guide",
    ],
    notIncluded: [
      "Full website",
      "Long-form copywriting",
      "Trademark research",
      "Packaging system",
      "Large campaign system",
      "Extensive subbrand architecture",
    ],
    whatWeNeed: [
      "Business context",
      "Existing assets",
      "Customer type",
      "Competitors or references",
      "Offer or service list",
      "Approval feedback",
    ],
    timeline: "Typically 2 to 4 weeks depending on scope.",
    process: [
      "Discovery call",
      "Direction concepts",
      "Refinement rounds",
      "Brand guide and file exports",
    ],
    cta: { label: "Request Brand Foundation Core", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["brand-foundation-starter", "brand-foundation-complete", "brand-and-website-growth-system"],
      needs: ["look-more-credible", "rebrand-and-relaunch"],
      industries: ["professional-services", "retail", "tech-and-saas"],
    },
    tags: ["brand", "identity", "system"],
  },
  {
    slug: "brand-foundation-complete",
    name: "Brand Foundation Complete",
    category: "brand-systems",
    price: 4500,
    priceLabel: "Starting at CA$4,500",
    billing: "project",
    outcome:
      "Feel like a serious business, not a side project, and give the team a brand system it can actually use every day.",
    bestFor:
      "Established businesses, launches, rebrands, and teams preparing for a stronger public presence.",
    whatYouReceive: [
      "Discovery session",
      "Positioning and audience notes",
      "Logo suite or identity system",
      "Colour and typography system",
      "Messaging framework",
      "Tone and voice direction",
      "Brand guide",
      "Social identity direction",
      "Launch-ready asset direction",
      "Basic print or sales material direction where appropriate",
      "File exports",
    ],
    whatThisHelpsFix: [
      "Outgrown brand identity",
      "Unclear positioning",
      "Lack of messaging discipline",
    ],
    notIncluded: [
      "Full website build",
      "Ongoing marketing management",
      "Paid advertising",
      "Trademark or legal review",
      "Deep market research",
      "Large multi-brand architecture unless quoted separately",
    ],
    whatWeNeed: [
      "Business background",
      "Decision maker access",
      "Existing assets",
      "Offer or service details",
      "Audience context",
      "Feedback during review rounds",
    ],
    timeline: "Typically 4 to 6 weeks depending on complexity.",
    process: [
      "Discovery session",
      "Positioning and direction",
      "Identity system and messaging",
      "Brand guide and final exports",
    ],
    cta: { label: "Request Complete Brand Foundation", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["brand-foundation-core", "complete-business-presence-system", "brand-and-website-growth-system"],
      needs: ["rebrand-and-relaunch", "look-more-credible"],
      industries: ["professional-services", "tech-and-saas", "hospitality"],
    },
    tags: ["brand", "identity", "premium"],
  },

  // ----------------------- WEBSITE SYSTEMS -----------------------
  {
    slug: "website-foundation-starter",
    name: "Website Foundation Starter",
    category: "website-systems",
    price: 2500,
    priceLabel: "Starting at CA$2,500",
    billing: "project",
    outcome:
      "Turn your website into something a stranger can trust in ten seconds and act on in thirty.",
    bestFor:
      "Small businesses, local businesses, service providers, and early-stage brands that need a clean web presence.",
    whatYouReceive: [
      "Website structure planning",
      "Up to 5 core page sections or pages depending on platform and scope",
      "Homepage direction",
      "Service or offer presentation",
      "Contact or booking path",
      "Mobile-conscious design",
      "Basic SEO title and meta guidance",
      "Launch checklist",
    ],
    whatThisHelpsFix: [
      "No website or weak placeholder",
      "Website that does not explain the offer",
      "No clear contact or booking path",
    ],
    notIncluded: [
      "Large custom website",
      "Advanced integrations",
      "Custom ecommerce",
      "Full copywriting unless included",
      "Ongoing maintenance",
      "Complex SEO strategy",
    ],
    whatWeNeed: [
      "Brand assets",
      "Website copy or raw content",
      "Service list",
      "Contact details",
      "Images if available",
      "Primary CTA",
    ],
    timeline: "Typically 2 to 4 weeks after content is ready.",
    process: [
      "Request the project",
      "Kickoff and structure plan",
      "Design and build",
      "Review and launch",
    ],
    cta: { label: "Request Website Starter", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["website-growth-build", "website-system-complete", "website-audit"],
      needs: ["website-doesnt-convert", "starting-a-new-business"],
      industries: ["local-services", "retail", "professional-services"],
    },
    tags: ["website", "starter"],
  },
  {
    slug: "website-growth-build",
    name: "Website Growth Build",
    category: "website-systems",
    price: 4500,
    priceLabel: "Starting at CA$4,500",
    billing: "project",
    outcome:
      "Turn your website into a real sales tool that explains the offer, builds trust, and guides visitors straight to booking or buying.",
    bestFor:
      "Businesses with real offers, multiple services, portfolio proof, and a need for stronger conversion flow.",
    whatYouReceive: [
      "Website strategy and structure",
      "Homepage design",
      "Core service pages",
      "About and contact flow",
      "Booking or inquiry pathway",
      "Portfolio or proof section",
      "Mobile-first layout",
      "Basic SEO structure",
      "Launch support",
    ],
    whatThisHelpsFix: [
      "Confusing service presentation",
      "Weak conversion path",
      "No proof or portfolio structure",
    ],
    notIncluded: [
      "Complex custom app functionality",
      "Large content library",
      "Custom ecommerce",
      "Ongoing SEO campaign",
      "Ongoing content publishing",
      "Third-party paid tool subscriptions",
    ],
    whatWeNeed: [
      "Brand files",
      "Content or content notes",
      "Service details",
      "Portfolio assets if available",
      "Desired customer action",
      "Feedback and approvals",
    ],
    timeline: "Typically 4 to 8 weeks depending on page count and content readiness.",
    process: [
      "Kickoff and strategy",
      "Structure and wireframes",
      "Design and build",
      "Review and launch",
    ],
    cta: { label: "Request Website Growth Build", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["website-foundation-starter", "website-system-complete", "brand-and-website-growth-system"],
      needs: ["website-doesnt-convert", "look-more-credible"],
      industries: ["professional-services", "tech-and-saas", "retail"],
    },
    tags: ["website", "growth"],
  },
  {
    slug: "website-system-complete",
    name: "Website System Complete",
    category: "website-systems",
    price: 7500,
    priceLabel: "Starting at CA$7,500",
    billing: "project",
    outcome:
      "Give the business a website built to scale, so more services, more locations, and more content never break the experience.",
    bestFor:
      "Established businesses, multi-service companies, ecommerce-adjacent brands, and companies needing industry or need-based navigation.",
    whatYouReceive: [
      "Website architecture",
      "Homepage",
      "Service marketplace or service pages",
      "Portfolio or proof system",
      "Blog or content hub structure",
      "Contact and booking paths",
      "Shop, Stripe, or Calendly routing where appropriate",
      "SEO metadata framework",
      "Mobile-first responsive system",
      "Launch support",
    ],
    whatThisHelpsFix: [
      "Outgrown website structure",
      "Need for industry or need-based navigation",
      "Multiple services without clear architecture",
    ],
    notIncluded: [
      "Custom customer portal",
      "Custom backend application",
      "Full ecommerce build",
      "Paid API integrations",
      "Ongoing marketing",
      "Copywriting beyond agreed scope",
    ],
    whatWeNeed: [
      "Business structure",
      "Service menu",
      "Brand assets",
      "Content inventory",
      "Portfolio assets",
      "Technical access where required",
    ],
    timeline: "Typically 6 to 12 weeks depending on scope.",
    process: [
      "Discovery and architecture",
      "Structure and content map",
      "Design and build",
      "Review and launch",
    ],
    cta: { label: "Request Website System", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["website-growth-build", "complete-business-presence-system"],
      needs: ["website-doesnt-convert", "rebrand-and-relaunch"],
      industries: ["professional-services", "tech-and-saas"],
    },
    tags: ["website", "premium", "system"],
  },

  // ----------------------- BRAND AND WEBSITE PACKAGES -----------------------
  {
    slug: "brand-and-website-launch-foundation",
    name: "Brand and Website Launch Foundation",
    category: "brand-and-website-packages",
    price: 4500,
    priceLabel: "Starting at CA$4,500",
    billing: "project",
    outcome:
      "Launch with a brand and website that already look like a real business, not a placeholder built in a weekend.",
    bestFor:
      "New businesses, relaunches, founders, and small companies that need to look credible from the start.",
    whatYouReceive: [
      "Brand direction",
      "Logo or identity foundation",
      "Colour and type direction",
      "Core messaging notes",
      "Website structure",
      "Professional website design",
      "Contact or booking flow",
      "Basic launch assets",
      "Launch checklist",
    ],
    whatThisHelpsFix: [
      "Trying to launch with disconnected pieces",
      "No brand and no website",
      "Unclear public presence",
    ],
    notIncluded: [
      "Large rebrand",
      "Large website",
      "Ongoing marketing",
      "Custom ecommerce",
      "Paid advertising",
      "Deep SEO campaign",
    ],
    whatWeNeed: [
      "Business name",
      "Offer details",
      "Audience details",
      "Existing assets if any",
      "Content or raw notes",
      "Decision maker feedback",
    ],
    timeline: "Typically 4 to 8 weeks.",
    process: [
      "Kickoff and direction",
      "Brand and structure",
      "Design and build",
      "Review and launch",
    ],
    cta: { label: "Request Launch Foundation", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["brand-and-website-growth-system", "complete-business-presence-system", "brand-foundation-core"],
      needs: ["starting-a-new-business", "rebrand-and-relaunch"],
      industries: ["local-services", "professional-services", "retail"],
    },
    tags: ["brand", "website", "launch"],
  },
  {
    slug: "brand-and-website-growth-system",
    name: "Brand and Website Growth System",
    category: "brand-and-website-packages",
    price: 7500,
    priceLabel: "Starting at CA$7,500",
    billing: "project",
    outcome:
      "Stop losing sales conversations to how the business looks online. Show up as the safer, more credible choice.",
    bestFor:
      "Existing businesses that have outgrown their current brand or website.",
    whatYouReceive: [
      "Brand review and strategy direction",
      "Identity system",
      "Messaging framework",
      "Website architecture",
      "Homepage",
      "Core service pages",
      "About and contact flow",
      "Portfolio or proof structure",
      "Mobile-first website design",
      "Basic SEO metadata framework",
      "Launch support",
    ],
    whatThisHelpsFix: [
      "Outgrown brand and website",
      "Weak sales conversation support",
      "Disconnected messaging",
    ],
    notIncluded: [
      "Ongoing marketing retainer",
      "Complex app functionality",
      "Custom ecommerce",
      "Large content production",
      "Paid advertising",
    ],
    whatWeNeed: [
      "Existing brand assets",
      "Existing website",
      "Offer or service menu",
      "Portfolio or proof materials",
      "Customer context",
      "Feedback and approvals",
    ],
    timeline: "Typically 6 to 10 weeks.",
    process: [
      "Strategy and brand direction",
      "Website architecture",
      "Design and build",
      "Review and launch",
    ],
    cta: { label: "Request Growth System", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["brand-and-website-launch-foundation", "complete-business-presence-system", "website-growth-build"],
      needs: ["rebrand-and-relaunch", "look-more-credible"],
      industries: ["professional-services", "tech-and-saas", "retail"],
    },
    tags: ["brand", "website", "growth"],
  },
  {
    slug: "complete-business-presence-system",
    name: "Complete Business Presence System",
    category: "brand-and-website-packages",
    price: 10000,
    priceLabel: "Starting at CA$10,000",
    billing: "project",
    outcome:
      "Give the entire business a public presence that finally matches what it is capable of, and that a team can grow into.",
    bestFor:
      "Established businesses, multi-service companies, regional brands, and companies preparing for significant growth.",
    whatYouReceive: [
      "Brand strategy direction",
      "Identity system",
      "Messaging and positioning framework",
      "Website system",
      "Service or offer architecture",
      "Portfolio or proof structure",
      "Sales material direction",
      "Local visibility direction where relevant",
      "Shop, booking, or payment routing where relevant",
      "Launch support",
    ],
    whatThisHelpsFix: [
      "Fragmented business presence",
      "Sales materials that do not match the brand",
      "No clear architecture for growth",
    ],
    notIncluded: [
      "Custom client portal",
      "Large-scale ongoing marketing",
      "Paid media management",
      "Major backend software",
      "Advanced automation unless scoped",
    ],
    whatWeNeed: [
      "Full business context",
      "Service and offer structure",
      "Decision maker access",
      "Existing assets and content",
      "Project goals",
      "Timeline expectations",
    ],
    timeline: "Custom, typically 8 to 14 weeks or more depending on scope.",
    process: [
      "Strategy call and scope",
      "Brand and messaging system",
      "Website and sales architecture",
      "Launch and handoff",
    ],
    cta: { label: "Start a Project", kind: "book" },
    stripePaymentLink: "",
    related: {
      services: ["brand-and-website-growth-system", "brand-foundation-complete", "website-system-complete"],
      needs: ["rebrand-and-relaunch", "scaling-up"],
      industries: ["professional-services", "tech-and-saas", "hospitality"],
    },
    tags: ["premium", "brand", "website", "system"],
  },

  // ----------------------- LOCAL VISIBILITY -----------------------
  {
    slug: "google-business-profile-audit",
    name: "Google Business Profile Audit",
    category: "local-visibility",
    price: 250,
    priceLabel: "CA$250",
    billing: "one-time",
    outcome:
      "See exactly why your Google profile is losing local searchers, and get a short list of fixes that will make it work harder for you.",
    bestFor: "Local businesses with a Google Business Profile that may not be pulling its weight.",
    whatYouReceive: [
      "Profile details review",
      "Category and service scan",
      "Photo and post review",
      "Review presence check",
      "Priority fix summary",
      "Written recommendations",
    ],
    whatThisHelpsFix: [
      "Incomplete or weak local profile",
      "Wrong categories",
      "Missing service or photo coverage",
    ],
    notIncluded: [
      "Implementation",
      "Guaranteed rankings",
      "Review generation",
      "Ongoing posting",
      "Website SEO",
    ],
    whatWeNeed: [
      "Profile link",
      "Business basics",
      "Service area",
      "Main services",
    ],
    timeline: "3 to 5 business days.",
    process: [
      "Request the audit",
      "Send profile link and basics",
      "Kostaja reviews",
      "Receive written recommendations",
    ],
    cta: { label: "Request Audit", kind: "stripe" },
    stripePaymentLink: "",
    related: {
      services: ["google-business-profile-optimization", "google-business-profile-setup", "local-seo-snapshot"],
      needs: ["more-local-customers"],
      industries: ["local-services", "hospitality"],
    },
    tags: ["audit", "local", "gbp"],
  },
  {
    slug: "google-business-profile-setup",
    name: "Google Business Profile Setup",
    category: "local-visibility",
    price: 450,
    priceLabel: "CA$450",
    billing: "one-time",
    outcome:
      "Show up properly on Google Maps and local search, so nearby customers can actually find you, trust you, and contact you.",
    bestFor: "New local businesses or businesses without a real local profile set up.",
    whatYouReceive: [
      "Profile setup support",
      "Business details organization",
      "Category and service setup",
      "Description and link guidance",
      "Photo upload guidance",
      "Basic profile polish",
    ],
    whatThisHelpsFix: [
      "Missing or empty local profile",
      "Weak first impression in local search",
      "Inconsistent business details",
    ],
    notIncluded: [
      "Guaranteed rankings",
      "Ongoing management",
      "Review generation",
      "Photography",
      "SEO campaign",
    ],
    whatWeNeed: [
      "Business information",
      "Google access or profile link",
      "Business hours",
      "Service list",
      "Photos if available",
      "Website and booking links",
    ],
    timeline: "Usually 5 to 10 business days after required access and materials.",
    process: [
      "Request setup",
      "Send access and materials",
      "Profile structure and polish",
      "Handoff and posting guidance",
    ],
    cta: { label: "Start Setup", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["google-business-profile-optimization", "google-business-profile-growth-support", "local-business-launch-kit"],
      needs: ["more-local-customers", "starting-a-new-business"],
      industries: ["local-services", "hospitality"],
    },
    tags: ["local", "gbp", "setup"],
  },
  {
    slug: "google-business-profile-optimization",
    name: "Google Business Profile Optimization",
    category: "local-visibility",
    price: 650,
    priceLabel: "CA$650",
    billing: "one-time",
    outcome:
      "Make your existing Google profile earn its keep by pulling in more local calls, clicks, and directions.",
    bestFor: "Local businesses with an existing profile that needs to be sharper and more useful.",
    whatYouReceive: [
      "Existing profile review",
      "Category and service refinement",
      "Description improvement",
      "Photo and link guidance",
      "Profile structure polish",
      "Priority recommendations",
    ],
    whatThisHelpsFix: [
      "Stale, incomplete, or off-message profile",
      "Weak category or service coverage",
      "Inconsistent links and details",
    ],
    notIncluded: [
      "Guaranteed rankings",
      "Ongoing posting",
      "Review generation",
      "Photography",
      "Major website work",
    ],
    whatWeNeed: [
      "Profile access or link",
      "Service list",
      "Service area",
      "Photos if available",
      "Website and booking links",
    ],
    timeline: "Usually 7 to 10 business days.",
    process: [
      "Request optimization",
      "Send access and materials",
      "Refinement and polish",
      "Handoff with guidance",
    ],
    cta: { label: "Start Optimization", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["google-business-profile-audit", "google-business-profile-growth-support", "local-seo-snapshot"],
      needs: ["more-local-customers"],
      industries: ["local-services", "hospitality"],
    },
    tags: ["local", "gbp"],
  },
  {
    slug: "google-business-profile-growth-support",
    name: "Google Business Profile Growth Support",
    category: "local-visibility",
    price: 350,
    priceLabel: "CA$350/month",
    billing: "monthly",
    outcome: "Keep your Google profile alive and useful every month, so local visibility does not fade the moment you get busy running the business.",
    bestFor: "Local businesses that want a consistent ongoing local presence.",
    whatYouReceive: [
      "Monthly profile updates",
      "Post planning support",
      "Service and offer updates",
      "Photo guidance",
      "Review response support",
      "Local presence notes",
    ],
    whatThisHelpsFix: [
      "Stale profile",
      "Inconsistent posting",
      "Weak review presence",
    ],
    notIncluded: [
      "Guaranteed rankings",
      "Paid ads",
      "Daily posting",
      "Photography",
      "Fake reviews",
      "Review incentives",
    ],
    whatWeNeed: [
      "Profile access",
      "Business updates each month",
      "Photos when available",
    ],
    timeline: "Ongoing monthly cycle.",
    process: [
      "Subscribe monthly",
      "Monthly planning call or notes",
      "Profile updates and posts",
      "Monthly review",
    ],
    cta: { label: "Subscribe Monthly", kind: "contact" },
    stripePaymentLink: "",
    related: {
      services: ["google-business-profile-growth-support-plus", "google-business-profile-optimization"],
      needs: ["more-local-customers"],
      industries: ["local-services", "hospitality"],
    },
    tags: ["local", "gbp", "monthly"],
  },
  {
    slug: "google-business-profile-growth-support-plus",
    name: "Google Business Profile Growth Support Plus",
    category: "local-visibility",
    price: 650,
    priceLabel: "CA$650/month",
    billing: "monthly",
    outcome: "Keep your Google profile actively working for you every month, so local traffic keeps coming in even during busy weeks.",
    bestFor: "Active local businesses that want stronger ongoing visibility.",
    whatYouReceive: [
      "Enhanced monthly profile updates",
      "Regular post planning support",
      "Service and offer updates",
      "Photo direction and refresh notes",
      "Review response support",
      "Local visibility recommendations",
    ],
    whatThisHelpsFix: [
      "Inconsistent active local presence",
      "Weak local recommendations",
      "Stale offers and posts",
    ],
    notIncluded: [
      "Guaranteed rankings",
      "Paid ads",
      "Photography unless quoted separately",
      "Fake reviews",
      "Multi-location management unless scoped",
    ],
    whatWeNeed: [
      "Profile access",
      "Monthly updates",
      "Photo input",
      "Service and offer updates",
    ],
    timeline: "Ongoing monthly cycle.",
    process: [
      "Subscribe monthly",
      "Monthly planning",
      "Profile updates and posts",
      "Review and refresh",
    ],
    cta: { label: "Subscribe Monthly Plus", kind: "contact" },
    stripePaymentLink: "",
    related: {
      services: ["google-business-profile-growth-support", "google-business-profile-optimization"],
      needs: ["more-local-customers"],
      industries: ["local-services", "hospitality"],
    },
    tags: ["local", "gbp", "monthly"],
  },
  {
    slug: "local-visibility-starter-kit",
    name: "Local Visibility Starter Kit",
    category: "local-visibility",
    price: 550,
    priceLabel: "CA$550",
    billing: "one-time",
    outcome:
      "Turn every in-person moment into a review, a lead, or a repeat visit, without any awkward asks.",
    bestFor: "Local businesses that want a small set of practical visibility assets.",
    whatYouReceive: [
      "Review request card design",
      "Local offer card design",
      "QR code placement areas",
      "Print-ready PDF files",
      "Local visibility guidance",
      "One revision round",
    ],
    whatThisHelpsFix: [
      "No review request system",
      "No simple local offer asset",
      "Weak in-person visibility",
    ],
    notIncluded: [
      "Printing costs",
      "GBP implementation",
      "Review generation",
      "Ongoing posting",
      "Website SEO",
    ],
    whatWeNeed: [
      "Brand assets",
      "Offer details",
      "Review platform link",
      "Service area",
    ],
    timeline: "Typically 1 to 2 weeks.",
    process: [
      "Request the kit",
      "Send brand and offer materials",
      "Design and revision round",
      "Print-ready handoff",
    ],
    cta: { label: "Start Local Visibility Kit", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["local-business-launch-kit", "google-business-profile-optimization"],
      needs: ["more-local-customers"],
      industries: ["local-services", "hospitality"],
    },
    tags: ["local", "print"],
  },
  {
    slug: "local-business-launch-kit",
    name: "Local Business Launch Kit",
    category: "local-visibility",
    price: 1250,
    priceLabel: "CA$1,250",
    billing: "project",
    outcome:
      "Open the doors with a local presence that already looks established, so the first ninety days are noticeably easier.",
    bestFor: "Launching or relaunching local businesses.",
    whatYouReceive: [
      "Google Business Profile setup support",
      "Review request card design",
      "Local offer card design",
      "Basic launch messaging",
      "Print-ready PDF files",
      "One revision round",
    ],
    whatThisHelpsFix: [
      "Launching without local visibility",
      "No customer-facing in-person assets",
      "Weak first impression in the neighbourhood",
    ],
    notIncluded: [
      "Full website build",
      "Full brand identity",
      "Printing costs",
      "Paid ads",
      "Ongoing management",
    ],
    whatWeNeed: [
      "Brand assets",
      "Business basics",
      "Offer details",
      "Service area",
      "Photos if available",
    ],
    timeline: "Typically 2 to 3 weeks.",
    process: [
      "Kickoff",
      "Profile setup support",
      "Design and revision round",
      "Print-ready handoff",
    ],
    cta: { label: "Request Launch Kit", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["local-visibility-starter-kit", "google-business-profile-setup", "brand-and-website-launch-foundation"],
      needs: ["starting-a-new-business", "more-local-customers"],
      industries: ["local-services", "hospitality", "retail"],
    },
    tags: ["local", "launch", "print"],
  },

  // ----------------------- CONTENT AND SOCIAL -----------------------
  {
    slug: "social-profile-kit",
    name: "Social Profile Kit",
    category: "content-and-social",
    price: 350,
    priceLabel: "CA$350",
    billing: "one-time",
    outcome: "Make your social profiles look like they belong to the same business, so first-time visitors take you seriously.",
    bestFor: "Businesses with inconsistent social profile visuals.",
    whatYouReceive: [
      "Profile image design",
      "Banner or cover image design",
      "Platform-ready export files",
      "Visual consistency check",
      "One revision round",
      "Existing brand assets required",
    ],
    whatThisHelpsFix: [
      "Mismatched social profile visuals",
      "Weak first impression on social",
      "Inconsistent banners across platforms",
    ],
    notIncluded: [
      "Logo design",
      "Full brand identity",
      "Social media management",
      "Posting",
      "Content calendar",
    ],
    whatWeNeed: [
      "Brand assets",
      "Platform list",
      "Profile and banner direction",
    ],
    timeline: "Typically 1 to 2 weeks.",
    process: [
      "Request the kit",
      "Send brand assets and platform list",
      "Design and revision round",
      "Platform-ready exports",
    ],
    cta: { label: "Start Social Profile Kit", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["google-business-profile-post-pack", "marketing-support-retainer-starter"],
      needs: ["look-more-credible"],
      industries: ["retail", "professional-services", "hospitality"],
    },
    tags: ["social", "design"],
  },
  {
    slug: "google-business-profile-post-pack",
    name: "Google Business Profile Post Pack",
    category: "content-and-social",
    price: 250,
    priceLabel: "CA$250",
    billing: "one-time",
    outcome: "Keep your Google profile active with ready-to-post updates, so it never goes quiet the moment the week gets busy.",
    bestFor: "Local businesses that want a small batch of ready post drafts.",
    whatYouReceive: [
      "5 GBP post drafts",
      "Offer and update copy",
      "Service announcement copy",
      "Customer-facing wording",
      "Basic posting guidance",
      "One revision round",
    ],
    whatThisHelpsFix: [
      "Inactive profile posts",
      "Inconsistent offers or announcements",
    ],
    notIncluded: [
      "Posting",
      "Graphic design unless added",
      "Monthly management",
      "Guaranteed visibility",
    ],
    whatWeNeed: [
      "Business basics",
      "Offers and updates to highlight",
      "Service list",
    ],
    timeline: "Typically 5 to 7 business days.",
    process: [
      "Request the pack",
      "Send offers and updates",
      "Drafts and revision round",
      "Handoff",
    ],
    cta: { label: "Request Post Pack", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["google-business-profile-growth-support", "social-profile-kit"],
      needs: ["more-local-customers"],
      industries: ["local-services", "hospitality"],
    },
    tags: ["local", "content"],
  },
  {
    slug: "marketing-support-retainer-starter",
    name: "Marketing Support Retainer Starter",
    category: "marketing-support",
    price: 1250,
    priceLabel: "Starting at CA$1,250/month",
    billing: "monthly",
    outcome:
      "Stop being the marketing bottleneck. Get consistent, on-brand support without having to hire and manage a full team.",
    bestFor: "Businesses that already have a foundation and need help showing up consistently.",
    whatYouReceive: [
      "Monthly planning",
      "Content or campaign support",
      "Design support allocation",
      "Website update support where scoped",
      "Marketing priority check-in",
    ],
    whatThisHelpsFix: [
      "No consistent monthly marketing rhythm",
      "Backlog of small design and content needs",
      "No central marketing focus",
    ],
    notIncluded: [
      "Unlimited design",
      "Paid ad management unless scoped",
      "Full-time marketing management",
      "Major website rebuild",
    ],
    whatWeNeed: [
      "Brand assets",
      "Existing website",
      "Service list",
      "Monthly priorities",
    ],
    timeline: "Ongoing monthly cycle.",
    process: [
      "Onboarding call",
      "Monthly planning",
      "Execution and updates",
      "Monthly review",
    ],
    cta: { label: "Request Monthly Support", kind: "contact" },
    stripePaymentLink: "",
    related: {
      services: ["marketing-support-growth", "google-business-profile-growth-support"],
      needs: ["scaling-up", "look-more-credible"],
      industries: ["professional-services", "tech-and-saas", "retail"],
    },
    tags: ["monthly", "marketing"],
  },
  {
    slug: "marketing-support-growth",
    name: "Marketing Support Growth",
    category: "marketing-support",
    price: 2500,
    priceLabel: "Starting at CA$2,500/month",
    billing: "monthly",
    outcome:
      "Give the business a real monthly marketing rhythm, so campaigns actually ship and the pipeline stops going cold.",
    bestFor: "Active businesses that need a stronger marketing cadence.",
    whatYouReceive: [
      "Monthly strategy and planning",
      "Content calendar support",
      "Campaign creative support",
      "Sales material support",
      "Website update support",
      "Ongoing reporting notes",
    ],
    whatThisHelpsFix: [
      "Inconsistent campaigns",
      "Missing sales material support",
      "No marketing cadence",
    ],
    notIncluded: [
      "Paid ad spend",
      "Large video production",
      "Custom app development",
      "Unlimited revisions",
    ],
    whatWeNeed: [
      "Brand and content access",
      "Monthly priorities",
      "Sales context",
      "Approvals during the month",
    ],
    timeline: "Ongoing monthly cycle.",
    process: [
      "Onboarding call",
      "Monthly strategy and planning",
      "Execution",
      "Monthly review",
    ],
    cta: { label: "Request Growth Support", kind: "contact" },
    stripePaymentLink: "",
    related: {
      services: ["marketing-support-retainer-starter", "google-business-profile-growth-support-plus"],
      needs: ["scaling-up", "look-more-credible"],
      industries: ["professional-services", "tech-and-saas"],
    },
    tags: ["monthly", "marketing", "growth"],
  },

  // ----------------------- PRINT AND SALES MATERIALS -----------------------
  {
    slug: "business-card-design",
    name: "Business Card Design",
    category: "print-and-sales-materials",
    price: 250,
    priceLabel: "CA$250",
    billing: "one-time",
    outcome: "Hand people a card that makes them take the business seriously the second they read it.",
    bestFor: "Businesses that need a clean, on-brand business card.",
    whatYouReceive: [
      "Front and back card design",
      "Print-ready PDF",
      "Digital preview image",
      "One revision round",
      "Basic print guidance",
      "Existing brand assets required",
    ],
    whatThisHelpsFix: ["Outdated or off-brand cards", "Inconsistent in-person impression"],
    notIncluded: [
      "Logo design",
      "Printing costs",
      "Full brand identity",
      "Copywriting beyond basic card details",
    ],
    whatWeNeed: ["Brand assets", "Card details (name, title, contact)"],
    timeline: "Typically 5 to 7 business days.",
    process: ["Request the design", "Send brand and details", "Design and revision round", "Print-ready PDF"],
    cta: { label: "Start Business Card Design", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["one-page-sales-sheet", "flyer-design", "postcard-design"], needs: [], industries: [] },
    tags: ["print"],
  },
  {
    slug: "one-page-sales-sheet",
    name: "One-Page Sales Sheet",
    category: "print-and-sales-materials",
    price: 500,
    priceLabel: "CA$500",
    billing: "one-time",
    outcome: "Give prospects one clean page that explains the offer, so decisions get made faster and follow-ups get easier.",
    bestFor: "Businesses with one main offer or service to communicate.",
    whatYouReceive: [
      "One-page layout design",
      "Offer and service structure",
      "Print-ready PDF",
      "Digital sharing file",
      "One revision round",
      "Existing brand assets required",
    ],
    whatThisHelpsFix: ["No clear offer sheet", "Hard-to-share service description"],
    notIncluded: ["Full copywriting strategy", "Multi-page brochure", "Printing costs", "Photography"],
    whatWeNeed: ["Brand assets", "Offer details", "Pricing notes if applicable"],
    timeline: "Typically 1 to 2 weeks.",
    process: ["Request the project", "Send brand and offer details", "Design and revision round", "Print-ready handoff"],
    cta: { label: "Start Sales Sheet", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["pitch-deck-refinement", "business-card-design", "flyer-design"], needs: [], industries: [] },
    tags: ["print", "sales"],
  },
  {
    slug: "pitch-deck-refinement",
    name: "Pitch Deck Refinement",
    category: "print-and-sales-materials",
    price: 750,
    priceLabel: "CA$750",
    billing: "one-time",
    outcome: "Walk into the room with a deck that looks investable, so the numbers are what people actually focus on.",
    bestFor: "Founders and teams with an existing deck that needs to look stronger.",
    whatYouReceive: [
      "Existing deck review",
      "Slide structure polish",
      "Visual hierarchy improvement",
      "Design refinement",
      "Export-ready presentation file",
      "One revision round",
    ],
    whatThisHelpsFix: ["Cluttered slides", "Weak visual hierarchy", "Off-brand deck"],
    notIncluded: ["Full pitch strategy", "Financial modeling", "Investor narrative development", "Full rewrite unless quoted"],
    whatWeNeed: ["Existing deck", "Brand assets", "Audience and goal"],
    timeline: "Typically 1 to 2 weeks.",
    process: ["Request the project", "Send deck and brand assets", "Refinement and revision round", "Final export"],
    cta: { label: "Start Pitch Deck Refinement", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["one-page-sales-sheet", "brand-foundation-core"], needs: [], industries: ["tech-and-saas", "professional-services"] },
    tags: ["sales", "deck"],
  },
  {
    slug: "flyer-design",
    name: "Flyer Design",
    category: "print-and-sales-materials",
    price: 300,
    priceLabel: "CA$300",
    billing: "one-time",
    outcome: "Put out a flyer that actually gets picked up, read, and acted on instead of thrown away.",
    bestFor: "Businesses with a focused event, announcement, or offer to promote.",
    whatYouReceive: [
      "One flyer design",
      "Print-ready PDF",
      "Digital sharing file",
      "Message layout support",
      "One revision round",
      "Existing brand assets required",
    ],
    whatThisHelpsFix: ["No clean event or offer flyer", "Off-brand promotional materials"],
    notIncluded: ["Printing costs", "Photography", "Copywriting beyond supplied content", "Full campaign strategy"],
    whatWeNeed: ["Brand assets", "Flyer message and details"],
    timeline: "Typically 5 to 7 business days.",
    process: ["Request the project", "Send brand and message", "Design and revision round", "Print-ready handoff"],
    cta: { label: "Start Flyer Design", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["postcard-design", "menu-design"], needs: [], industries: ["local-services", "hospitality"] },
    tags: ["print"],
  },
  {
    slug: "menu-design",
    name: "Menu Design",
    category: "print-and-sales-materials",
    price: 450,
    priceLabel: "CA$450",
    billing: "one-time",
    outcome: "Give guests a menu that is easy to read, easy to order from, and quietly guides them toward higher-margin items.",
    bestFor: "Restaurants, cafes, and service businesses with menu items to organize.",
    whatYouReceive: [
      "One menu design",
      "Section and item layout",
      "Print-ready PDF",
      "Digital sharing file",
      "One revision round",
      "Existing brand assets required",
    ],
    whatThisHelpsFix: ["Cluttered or off-brand menus", "Poor menu hierarchy"],
    notIncluded: ["Printing costs", "Food photography", "Menu strategy", "Full brand identity"],
    whatWeNeed: ["Brand assets", "Menu content", "Pricing"],
    timeline: "Typically 1 to 2 weeks.",
    process: ["Request the project", "Send brand and menu content", "Design and revision round", "Print-ready handoff"],
    cta: { label: "Start Menu Design", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["flyer-design", "postcard-design"], needs: [], industries: ["hospitality"] },
    tags: ["print", "menu"],
  },
  {
    slug: "postcard-design",
    name: "Postcard Design",
    category: "print-and-sales-materials",
    price: 300,
    priceLabel: "CA$300",
    billing: "one-time",
    outcome: "Land in mailboxes or hands with a postcard that gets kept, remembered, and acted on.",
    bestFor: "Businesses with a postcard, thank-you, or promo use case.",
    whatYouReceive: [
      "Front and back design",
      "Print-ready PDF",
      "Digital sharing file",
      "Message layout support",
      "One revision round",
      "Existing brand assets required",
    ],
    whatThisHelpsFix: ["No branded postcard asset", "Weak follow-up touchpoint"],
    notIncluded: ["Printing costs", "Mailing service", "List building", "Copywriting beyond supplied details"],
    whatWeNeed: ["Brand assets", "Card message and details"],
    timeline: "Typically 5 to 7 business days.",
    process: ["Request the project", "Send brand and message", "Design and revision round", "Print-ready handoff"],
    cta: { label: "Start Postcard Design", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["business-card-design", "flyer-design"], needs: [], industries: ["local-services", "hospitality"] },
    tags: ["print"],
  },
  {
    slug: "sticker-design",
    name: "Sticker Design",
    category: "print-and-sales-materials",
    price: 200,
    priceLabel: "CA$200",
    billing: "one-time",
    outcome: "Turn packaging, laptops, and giveaways into small brand moments customers actually want to keep and share.",
    bestFor: "Businesses with packaging, giveaway, or label use cases.",
    whatYouReceive: [
      "One sticker or label design",
      "Print-ready PDF",
      "Digital preview file",
      "Basic layout support",
      "One revision round",
      "Existing brand assets required",
    ],
    whatThisHelpsFix: ["No branded sticker or label asset"],
    notIncluded: ["Printing costs", "Packaging system", "Illustration", "Logo design"],
    whatWeNeed: ["Brand assets", "Sticker concept and message"],
    timeline: "Typically 3 to 5 business days.",
    process: ["Request the project", "Send brand and concept", "Design and revision round", "Print-ready handoff"],
    cta: { label: "Start Sticker Design", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["business-card-design", "postcard-design"], needs: [], industries: ["retail", "hospitality"] },
    tags: ["print", "sticker"],
  },
  {
    slug: "print-file-prep",
    name: "Print File Prep",
    category: "print-and-sales-materials",
    price: 150,
    priceLabel: "CA$150",
    billing: "one-time",
    outcome: "Get files that print right the first time, so the launch or event does not stall at the printer.",
    bestFor: "Businesses with a design file that needs to be print-ready.",
    whatYouReceive: [
      "Existing file review",
      "Size and margin check",
      "Bleed setup where possible",
      "Print-ready PDF export",
      "Basic file preparation",
      "One revision round",
    ],
    whatThisHelpsFix: ["Files that fail at the printer", "Missing bleed or margin"],
    notIncluded: ["New design", "Major layout redesign", "Printing costs", "Printer communication beyond basic guidance"],
    whatWeNeed: ["Existing file", "Print specifications", "Printer notes if available"],
    timeline: "Typically 2 to 4 business days.",
    process: ["Request the project", "Send file and specifications", "File preparation and review", "Print-ready handoff"],
    cta: { label: "Start Print File Prep", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["business-card-design", "menu-design", "flyer-design"], needs: [], industries: [] },
    tags: ["print", "fileprep"],
  },

  // ----------------------- DIGITAL PRODUCT SUPPORT -----------------------
  {
    slug: "digital-product-launch-support",
    name: "Digital Product Launch Support",
    category: "digital-product-support",
    price: 650,
    priceLabel: "CA$650",
    billing: "one-time",
    outcome: "Turn what you already know into a digital product that looks polished enough to charge real money for.",
    bestFor: "Creators and small businesses with a digital resource to package.",
    whatYouReceive: [
      "One digital product reviewed",
      "Structure and presentation support",
      "Sales description refinement",
      "Basic packaging guidance",
      "Launch checklist included",
      "One revision round",
    ],
    whatThisHelpsFix: ["Unpolished digital product", "Weak product structure", "Sales-ready packaging"],
    notIncluded: ["Full product creation from scratch", "Complex funnel build", "Custom ecommerce", "Shopify product upload unless added", "Advanced automation"],
    whatWeNeed: ["Existing product file", "Brand assets", "Target buyer notes"],
    timeline: "Typically 1 to 2 weeks.",
    process: ["Request the project", "Send product and brand materials", "Review and revision round", "Launch handoff"],
    cta: { label: "Start Digital Product Support", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["digital-product-listing-copy", "digital-product-cover-design", "digital-product-mockup-design"], needs: [], industries: ["tech-and-saas", "professional-services"] },
    tags: ["digital-product"],
  },
  {
    slug: "digital-product-listing-copy",
    name: "Digital Product Listing Copy",
    category: "digital-product-support",
    price: 250,
    priceLabel: "CA$250",
    billing: "one-time",
    outcome: "Explain your digital product in a way that makes the value obvious and the price feel easy to say yes to.",
    bestFor: "Creators with a digital product who need better listing copy.",
    whatYouReceive: [
      "Product title support",
      "Short description copy",
      "Feature list writing",
      "Value summary",
      "Usage notes",
      "One revision round",
    ],
    whatThisHelpsFix: ["Weak product description", "Unclear product value"],
    notIncluded: ["Product design", "Mockup design", "Shopify upload", "Full sales page"],
    whatWeNeed: ["Product details", "Buyer type", "Key features"],
    timeline: "Typically 5 to 7 business days.",
    process: ["Request the project", "Send product details", "Draft and revision round", "Handoff"],
    cta: { label: "Start Listing Copy", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["digital-product-launch-support", "digital-product-cover-design"], needs: [], industries: ["tech-and-saas"] },
    tags: ["digital-product", "copy"],
  },
  {
    slug: "digital-product-cover-design",
    name: "Digital Product Cover Design",
    category: "digital-product-support",
    price: 300,
    priceLabel: "CA$300",
    billing: "one-time",
    outcome: "Give your digital product a cover that makes people click, save, and buy on sight.",
    bestFor: "Creators who need a stronger cover or thumbnail for one product.",
    whatYouReceive: [
      "One product cover design",
      "Digital thumbnail version",
      "Clean product presentation",
      "Brand-aligned visual direction",
      "Export-ready image file",
      "One revision round",
    ],
    whatThisHelpsFix: ["Weak or off-brand product cover", "Low-credibility thumbnail"],
    notIncluded: ["Full product design", "Listing copy", "Mockup set", "Shopify upload"],
    whatWeNeed: ["Product details", "Brand assets", "Cover direction notes"],
    timeline: "Typically 5 to 7 business days.",
    process: ["Request the project", "Send brand and product details", "Design and revision round", "Export handoff"],
    cta: { label: "Start Cover Design", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["digital-product-mockup-design", "digital-product-listing-copy"], needs: [], industries: ["tech-and-saas"] },
    tags: ["digital-product", "design"],
  },
  {
    slug: "digital-product-mockup-design",
    name: "Digital Product Mockup Design",
    category: "digital-product-support",
    price: 350,
    priceLabel: "CA$350",
    billing: "one-time",
    outcome: "Show your digital product in a way that makes it feel real, valuable, and worth paying for.",
    bestFor: "Creators who need a sales-ready visual for a digital product.",
    whatYouReceive: [
      "One product mockup design",
      "Digital sales visual",
      "Product preview layout",
      "Brand-aligned presentation",
      "Export-ready image file",
      "One revision round",
    ],
    whatThisHelpsFix: ["No sales-ready product visual", "Weak listing visual"],
    notIncluded: ["Full product design", "Product cover", "Listing copy", "Shopify upload"],
    whatWeNeed: ["Product file or cover", "Brand assets", "Mockup direction notes"],
    timeline: "Typically 5 to 7 business days.",
    process: ["Request the project", "Send product and brand assets", "Design and revision round", "Export handoff"],
    cta: { label: "Start Mockup Design", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["digital-product-cover-design", "digital-product-listing-copy"], needs: [], industries: ["tech-and-saas"] },
    tags: ["digital-product", "design"],
  },

  // ----------------------- PLATFORM BRAND KITS -----------------------
  {
    slug: "canva-brand-presence-kit",
    name: "Canva Brand Presence Kit",
    category: "platform-brand-kits",
    price: 199,
    priceLabel: "CA$199",
    billing: "one-time",
    outcome: "Stop looking off-brand inside Canva. Post, share, and design with a system that already matches the business.",
    bestFor: "Solo operators and small businesses using Canva.",
    whatYouReceive: [
      "Canva-ready brand direction",
      "Font direction",
      "Colour direction",
      "Logo direction or setup",
      "Mini vision board",
      "One revision round",
    ],
    whatThisHelpsFix: ["Inconsistent Canva content", "No clear brand direction inside Canva"],
    notIncluded: ["Full brand strategy", "Full logo suite", "Copywriting", "Social media management", "Posting", "Website design"],
    whatWeNeed: ["Business basics", "Existing brand notes", "Style preferences"],
    timeline: "Typically 1 to 2 weeks.",
    process: ["Request the kit", "Send direction notes", "Design and revision round", "Canva-ready handoff"],
    cta: { label: "Start Canva Brand Presence Kit", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["canva-brand-presence-kit-plus", "canva-brand-presence-kit-complete"], needs: ["look-more-credible"], industries: ["professional-services", "retail"] },
    tags: ["platform-kit", "canva"],
  },
  {
    slug: "canva-brand-presence-kit-plus",
    name: "Canva Brand Presence Kit Plus",
    category: "platform-brand-kits",
    price: 299,
    priceLabel: "CA$299",
    billing: "one-time",
    outcome: "Have on-brand Canva templates ready to go, so posting takes minutes instead of half an afternoon.",
    bestFor: "Businesses that need a brand direction and a small set of templates.",
    whatYouReceive: [
      "Canva-ready brand direction",
      "Mini vision board",
      "5 directed Canva templates",
      "Font and colour direction",
      "Logo direction or setup",
      "One revision round",
    ],
    whatThisHelpsFix: ["No working Canva templates", "Inconsistent in-platform content"],
    notIncluded: ["Full brand strategy", "Social management", "Posting", "Unlimited templates"],
    whatWeNeed: ["Business basics", "Brand notes", "Template use cases"],
    timeline: "Typically 1 to 3 weeks.",
    process: ["Request the kit", "Send direction notes", "Design and revision round", "Canva-ready handoff"],
    cta: { label: "Start Canva Brand Presence Kit Plus", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["canva-brand-presence-kit", "canva-brand-presence-kit-complete"], needs: ["look-more-credible"], industries: ["professional-services", "retail"] },
    tags: ["platform-kit", "canva"],
  },
  {
    slug: "canva-brand-presence-kit-complete",
    name: "Canva Brand Presence Kit Complete",
    category: "platform-brand-kits",
    price: 399,
    priceLabel: "CA$399",
    billing: "one-time",
    outcome: "Run the whole business inside Canva without ever looking off-brand, no matter who is designing today.",
    bestFor: "Businesses that want a stronger set of Canva templates and direction.",
    whatYouReceive: [
      "Canva-ready brand direction",
      "Mini vision board",
      "10 directed Canva templates",
      "Font and colour direction",
      "Logo direction or setup",
      "One revision round",
    ],
    whatThisHelpsFix: ["Insufficient template coverage", "Inconsistent visual presence"],
    notIncluded: ["Full brand identity", "Social management", "Posting", "Unlimited revisions"],
    whatWeNeed: ["Business basics", "Brand notes", "Template use cases"],
    timeline: "Typically 2 to 3 weeks.",
    process: ["Request the kit", "Send direction notes", "Design and revision round", "Canva-ready handoff"],
    cta: { label: "Start Canva Brand Presence Kit Complete", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["canva-brand-presence-kit-plus", "brand-foundation-starter"], needs: ["look-more-credible"], industries: ["professional-services", "retail"] },
    tags: ["platform-kit", "canva"],
  },
  {
    slug: "godaddy-studio-brand-presence-kit",
    name: "GoDaddy Studio Brand Presence Kit",
    category: "platform-brand-kits",
    price: 249,
    priceLabel: "CA$249",
    billing: "one-time",
    outcome: "Post from GoDaddy Studio with content that finally looks like it belongs to the same business.",
    bestFor: "Solo operators using GoDaddy Studio.",
    whatYouReceive: [
      "GoDaddy Studio-ready brand direction",
      "Font direction",
      "Colour direction",
      "Logo direction or setup",
      "Mini vision board",
      "One revision round",
    ],
    whatThisHelpsFix: ["Inconsistent Studio content"],
    notIncluded: ["Full brand strategy", "Posting", "Website design"],
    whatWeNeed: ["Business basics", "Brand notes"],
    timeline: "Typically 1 to 2 weeks.",
    process: ["Request the kit", "Send notes", "Design and revision round", "Studio-ready handoff"],
    cta: { label: "Start Studio Brand Presence Kit", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["godaddy-studio-brand-presence-kit-plus", "godaddy-studio-brand-presence-kit-complete"], needs: ["look-more-credible"], industries: [] },
    tags: ["platform-kit", "studio"],
  },
  {
    slug: "godaddy-studio-brand-presence-kit-plus",
    name: "GoDaddy Studio Brand Presence Kit Plus",
    category: "platform-brand-kits",
    price: 349,
    priceLabel: "CA$349",
    billing: "one-time",
    outcome: "Have on-brand GoDaddy Studio templates ready, so daily content is quick and stays consistent.",
    bestFor: "Businesses using GoDaddy Studio that need template coverage.",
    whatYouReceive: [
      "GoDaddy Studio-ready brand direction",
      "Mini vision board",
      "5 directed Studio templates",
      "Font and colour direction",
      "Logo direction or setup",
      "One revision round",
    ],
    whatThisHelpsFix: ["No working Studio templates"],
    notIncluded: ["Posting", "Unlimited templates"],
    whatWeNeed: ["Business basics", "Brand notes", "Template use cases"],
    timeline: "Typically 1 to 3 weeks.",
    process: ["Request the kit", "Send notes", "Design and revision round", "Studio-ready handoff"],
    cta: { label: "Start Studio Brand Presence Kit Plus", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["godaddy-studio-brand-presence-kit", "godaddy-studio-brand-presence-kit-complete"], needs: [], industries: [] },
    tags: ["platform-kit", "studio"],
  },
  {
    slug: "godaddy-studio-brand-presence-kit-complete",
    name: "GoDaddy Studio Brand Presence Kit Complete",
    category: "platform-brand-kits",
    price: 449,
    priceLabel: "CA$449",
    billing: "one-time",
    outcome: "Run day-to-day content inside GoDaddy Studio without the brand ever drifting off course.",
    bestFor: "Businesses using Studio that want a stronger template set.",
    whatYouReceive: [
      "GoDaddy Studio-ready brand direction",
      "Mini vision board",
      "10 directed Studio templates",
      "Font and colour direction",
      "Logo direction or setup",
      "One revision round",
    ],
    whatThisHelpsFix: ["Insufficient template coverage"],
    notIncluded: ["Posting", "Unlimited templates"],
    whatWeNeed: ["Business basics", "Brand notes", "Template use cases"],
    timeline: "Typically 2 to 3 weeks.",
    process: ["Request the kit", "Send notes", "Design and revision round", "Studio-ready handoff"],
    cta: { label: "Start Studio Brand Presence Kit Complete", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["godaddy-studio-brand-presence-kit-plus"], needs: [], industries: [] },
    tags: ["platform-kit", "studio"],
  },
  {
    slug: "adobe-brand-presence-kit",
    name: "Adobe Brand Presence Kit",
    category: "platform-brand-kits",
    price: 299,
    priceLabel: "CA$299",
    billing: "one-time",
    outcome: "Give the team an Adobe-ready brand direction, so every piece of content lands on-brand the first time.",
    bestFor: "Teams using Adobe Express or Adobe creative tools.",
    whatYouReceive: [
      "Adobe-ready brand direction",
      "Font direction",
      "Colour direction",
      "Logo direction or setup",
      "Mini vision board",
      "One revision round",
    ],
    whatThisHelpsFix: ["Inconsistent in-Adobe content"],
    notIncluded: ["Full brand strategy", "Posting"],
    whatWeNeed: ["Business basics", "Brand notes"],
    timeline: "Typically 1 to 2 weeks.",
    process: ["Request the kit", "Send notes", "Design and revision round", "Adobe-ready handoff"],
    cta: { label: "Start Adobe Brand Presence Kit", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["adobe-brand-presence-kit-plus", "adobe-brand-presence-kit-complete"], needs: [], industries: [] },
    tags: ["platform-kit", "adobe"],
  },
  {
    slug: "adobe-brand-presence-kit-plus",
    name: "Adobe Brand Presence Kit Plus",
    category: "platform-brand-kits",
    price: 399,
    priceLabel: "CA$399",
    billing: "one-time",
    outcome: "Give the team on-brand Adobe templates, so content ships faster without a designer touching every asset.",
    bestFor: "Teams using Adobe tools that need template coverage.",
    whatYouReceive: [
      "Adobe-ready brand direction",
      "Mini vision board",
      "5 directed Adobe templates",
      "Font and colour direction",
      "Logo direction or setup",
      "One revision round",
    ],
    whatThisHelpsFix: ["No working Adobe templates"],
    notIncluded: ["Posting", "Unlimited templates"],
    whatWeNeed: ["Business basics", "Brand notes", "Template use cases"],
    timeline: "Typically 1 to 3 weeks.",
    process: ["Request the kit", "Send notes", "Design and revision round", "Adobe-ready handoff"],
    cta: { label: "Start Adobe Brand Presence Kit Plus", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["adobe-brand-presence-kit", "adobe-brand-presence-kit-complete"], needs: [], industries: [] },
    tags: ["platform-kit", "adobe"],
  },
  {
    slug: "adobe-brand-presence-kit-complete",
    name: "Adobe Brand Presence Kit Complete",
    category: "platform-brand-kits",
    price: 499,
    priceLabel: "CA$499",
    billing: "one-time",
    outcome: "Give the team a full set of Adobe templates, so every campaign, post, and asset already looks on-brand.",
    bestFor: "Teams using Adobe tools that want a stronger template set.",
    whatYouReceive: [
      "Adobe-ready brand direction",
      "Mini vision board",
      "10 directed Adobe templates",
      "Font and colour direction",
      "Logo direction or setup",
      "One revision round",
    ],
    whatThisHelpsFix: ["Insufficient template coverage"],
    notIncluded: ["Posting", "Unlimited templates"],
    whatWeNeed: ["Business basics", "Brand notes", "Template use cases"],
    timeline: "Typically 2 to 3 weeks.",
    process: ["Request the kit", "Send notes", "Design and revision round", "Adobe-ready handoff"],
    cta: { label: "Start Adobe Brand Presence Kit Complete", kind: "quote" },
    stripePaymentLink: "",
    related: { services: ["adobe-brand-presence-kit-plus"], needs: [], industries: [] },
    tags: ["platform-kit", "adobe"],
  },

  // ----------------------- BUSINESS LAUNCH SUPPORT -----------------------
  {
    slug: "business-launch-strategy-session",
    name: "Business Launch Strategy Session",
    category: "business-launch-support",
    price: 250,
    priceLabel: "CA$250",
    billing: "one-time",
    outcome:
      "Leave the call knowing exactly what to build first, second, and third, so the launch actually happens on time.",
    bestFor:
      "Founders preparing for a launch who need help shaping brand, website, and visibility priorities.",
    whatYouReceive: [
      "30-minute launch-focused session",
      "Review of brand, website, and visibility readiness",
      "Recommended sequence of next steps",
      "Clear follow-up direction",
    ],
    whatThisHelpsFix: ["Unclear launch sequence", "Scattered launch decisions"],
    notIncluded: ["Brand identity", "Website build", "Implementation"],
    whatWeNeed: ["Business name", "Launch goal", "Existing assets if any", "Timeline"],
    timeline: "Call happens at booked Calendly time.",
    process: ["Book the session", "Send launch context", "Meet for the working session", "Receive recommended sequence"],
    cta: { label: "Book Launch Session", kind: "book" },
    stripePaymentLink: "",
    related: {
      services: ["brand-and-website-launch-foundation", "local-business-launch-kit", "brand-foundation-starter"],
      needs: ["starting-a-new-business"],
      industries: ["local-services", "professional-services", "retail"],
    },
    tags: ["launch", "strategy"],
  },

  // ----------------------- ADDITIONAL LIGHT SERVICES (real estate friendly) -----------------------
  {
    slug: "review-request-card-design",
    name: "Review Request Card Design",
    category: "print-and-sales-materials",
    price: null,
    priceLabel: "Quote",
    billing: "one-time",
    outcome:
      "Turn happy customers into a steady stream of five-star reviews, without ever feeling awkward asking for one.",
    bestFor:
      "Local businesses and real estate professionals that want a steady review pipeline without gimmicks.",
    whatYouReceive: [
      "Front and back review request card design",
      "QR code area for direct review link",
      "Print-ready PDF",
      "Digital sharing file",
      "One revision round",
      "Existing brand assets required",
    ],
    whatThisHelpsFix: [
      "Inconsistent or missing review pipeline",
      "Customers who would leave a review but never get asked",
    ],
    notIncluded: [
      "Printing costs",
      "Review platform setup",
      "Review generation",
      "Fake reviews",
    ],
    whatWeNeed: [
      "Brand assets",
      "Review platform link",
      "Card message and details",
    ],
    timeline: "Typically 5 to 7 business days.",
    process: [
      "Request the project",
      "Send brand and review link",
      "Design and revision round",
      "Print-ready handoff",
    ],
    cta: { label: "Start Review Request Card", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["qr-code-card-design", "postcard-design", "local-visibility-starter-kit"],
      needs: ["more-local-customers"],
      industries: ["real-estate", "local-services", "home-services", "hospitality"],
    },
    tags: ["print", "local", "review"],
  },
  {
    slug: "qr-code-card-design",
    name: "QR Code Card Design",
    category: "print-and-sales-materials",
    price: null,
    priceLabel: "Quote",
    billing: "one-time",
    outcome:
      "Turn a printed card into a one-tap path to your booking, listing, review, or offer, so leads stop slipping away.",
    bestFor:
      "Real estate professionals, local services, and events that want one-tap connection from print to a digital action.",
    whatYouReceive: [
      "Front and back card design",
      "QR code generation and placement",
      "Print-ready PDF",
      "Digital sharing file",
      "One revision round",
      "Existing brand assets required",
    ],
    whatThisHelpsFix: [
      "Print assets that do not connect to a digital action",
      "Lost leads at the open house, event, or counter",
    ],
    notIncluded: ["Printing costs", "Landing page design", "Tracking analytics setup"],
    whatWeNeed: [
      "Brand assets",
      "Target link or action",
      "Card message and details",
    ],
    timeline: "Typically 3 to 5 business days.",
    process: [
      "Request the project",
      "Send brand and target link",
      "Design and revision round",
      "Print-ready handoff",
    ],
    cta: { label: "Start QR Code Card", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["review-request-card-design", "postcard-design", "business-card-design"],
      needs: ["more-local-customers"],
      industries: ["real-estate", "local-services", "home-services", "hospitality"],
    },
    tags: ["print", "local", "qr"],
  },
  {
    slug: "local-service-page-package",
    name: "Local Service Page Package",
    category: "website-systems",
    price: null,
    priceLabel: "Quote",
    billing: "project",
    outcome:
      "Get found for the exact services and areas that actually pay the bills, so more of the right leads call you first.",
    bestFor:
      "Real estate professionals, home services, and local businesses with multiple service areas or service lines.",
    whatYouReceive: [
      "Local service page structure",
      "On-page SEO direction",
      "Trust signal placement",
      "Contact and booking pathway per page",
      "Mobile-first design",
      "Launch support",
    ],
    whatThisHelpsFix: [
      "One-page site that cannot rank for multiple services or areas",
      "Generic service descriptions",
      "Weak conversion path inside service pages",
    ],
    notIncluded: [
      "Full website rebuild unless quoted",
      "Ongoing SEO campaign",
      "Paid advertising",
    ],
    whatWeNeed: [
      "Service or area list",
      "Brand assets",
      "Content or content notes",
      "Primary CTA per page",
    ],
    timeline: "Typically 2 to 5 weeks depending on page count.",
    process: [
      "Kickoff and scope",
      "Structure and content map",
      "Design and build",
      "Review and launch",
    ],
    cta: { label: "Request Service Page Package", kind: "quote" },
    stripePaymentLink: "",
    related: {
      services: ["website-foundation-starter", "website-growth-build", "google-business-profile-optimization"],
      needs: ["more-local-customers", "website-doesnt-convert"],
      industries: ["real-estate", "home-services", "local-services"],
    },
    tags: ["website", "local", "seo"],
  },

  // ----------------------- CUSTOM PROJECTS -----------------------
  {
    slug: "custom-project",
    name: "Custom Project",
    category: "custom-projects",
    price: null,
    priceLabel: "Quote",
    billing: "custom",
    outcome:
      "Get a scope, price, and timeline written in plain English, so a big or unusual project stops feeling risky to start.",
    bestFor:
      "Teams with a unique scope spanning brand, website, sales materials, launch, or visibility work.",
    whatYouReceive: [
      "Strategy call",
      "Scope and price proposal in plain English",
      "Clear deliverables list",
      "Project schedule",
    ],
    whatThisHelpsFix: ["Scope that does not match a standard package", "Multi-project planning"],
    notIncluded: ["Anything not explicitly written into the agreed scope"],
    whatWeNeed: ["Project context", "Goals and constraints", "Existing assets", "Decision maker access"],
    timeline: "Custom, agreed during scoping.",
    process: ["Book a strategy call", "Receive a written scope", "Approve and start", "Deliver in agreed phases"],
    cta: { label: "Start a Project", kind: "book" },
    stripePaymentLink: "",
    related: { services: ["complete-business-presence-system", "brand-and-website-growth-system"], needs: ["scaling-up"], industries: [] },
    tags: ["custom", "premium"],
  },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
export const getServicesByCategory = (categorySlug) =>
  SERVICES.filter((s) => s.category === categorySlug);
export const getServicesByTag = (tag) => SERVICES.filter((s) => (s.tags || []).includes(tag));
